import type { Character, CharacterSkill } from './character.ts';
import { CHARACTER_SCHEMA_VERSION } from './character.ts';
import { STAT_IDS } from './stats.ts';
import type { InventoryEntry } from '../rules/inventory.ts';
import { validateInventory } from '../rules/inventory.ts';
import { validateHotlist } from '../rules/hotlist.ts';
import { CRAWLER_HEALTH_BAR_SLOTS } from '../rules/health.ts';
import { isLegalSize } from '../rules/size.ts';
import type { PetCompanion } from './pet.ts';
import { validatePetCompanion } from './pet.ts';

/**
 * Hand-written runtime validator. No external schema library, so a
 * non-programmer can read exactly what is being checked and why.
 * Used by BOTH the repository (on load) and the importer (before touching the
 * library), so a malformed file can never reach the rest of the application.
 */
export interface ValidationReport {
  valid: boolean;
  errors: string[];
}

const MIN_SKILL_RANK = 1;
/** RULE DCC-SKILL-001 - Skill Ranks run 0-20. Source: Rules for Survival p.12. */
const MAX_SKILL_RANK = 20;

export function validateCharacter(value: unknown): ValidationReport {
  const errors: string[] = [];
  const push = (m: string) => errors.push(m);

  if (typeof value !== 'object' || value === null) {
    return { valid: false, errors: ['Character record is not an object.'] };
  }
  const c = value as Record<string, unknown>;

  if (typeof c['id'] !== 'string' || c['id'].length === 0) push('Missing character id.');
  if (c['kind'] !== 'character') push('Record kind must be "character".');
  if (typeof c['name'] !== 'string' || c['name'].trim().length === 0) push('Missing character name.');
  if (c['crawlerType'] !== 'human' && c['crawlerType'] !== 'animal') {
    push('crawlerType must be "human" or "animal".');
  }
  if (typeof c['schemaVersion'] !== 'number') {
    push('Missing schemaVersion.');
  } else if (c['schemaVersion'] > CHARACTER_SCHEMA_VERSION) {
    push(
      `Character was saved by a newer version of the app (schema ${c['schemaVersion']}, this app understands ${CHARACTER_SCHEMA_VERSION}).`,
    );
  }
  if (!isPositiveInt(c['level']) || Number(c['level']) > 250) push('level must be a whole number from 1 to 250.');
  const progression = c['statAdvancement'];
  if (progression !== undefined) {
    if (!progression || typeof progression !== 'object' || Array.isArray(progression)) push('statAdvancement must be an object.');
    else {
      const p = progression as Record<string, unknown>;
      if (!isPositiveInt(p['levelAccountedFor']) || Number(p['levelAccountedFor']) > 250) push('Invalid Stat Point Level baseline.');
      if (!isWholeNumber(p['unspentPoints']) || Number(p['unspentPoints']) > 747) push('Invalid unspent Stat Points.');
      if (typeof p['needsReview'] !== 'boolean') push('Invalid Stat Point review status.');
    }
  }
  const ceilings = c['skillRankLimits'];
  if (ceilings !== undefined && (!ceilings || typeof ceilings !== 'object' || Array.isArray(ceilings) || Object.entries(ceilings).some(([id, cap]) => !id || cap !== 20))) push('Skill Rank exceptions must name Skills with a Rank 20 ceiling.');
  if (!isPositiveInt(c['floor'])) push('floor must be a whole number of at least 1.');

  const stats = c['unenhancedStats'];
  if (typeof stats !== 'object' || stats === null) {
    push('Missing unenhancedStats.');
  } else {
    const s = stats as Record<string, unknown>;
    for (const stat of STAT_IDS) {
      const v = s[stat];
      if (!isPositiveInt(v)) push(`Stat ${stat} must be a whole number of at least 1.`);
    }
  }

  const skills = c['skills'];
  if (!Array.isArray(skills)) {
    push('skills must be a list.');
  } else {
    const seen = new Set<string>();
    skills.forEach((entry, i) => {
      if (typeof entry !== 'object' || entry === null) {
        push(`skills[${i}] is not an object.`);
        return;
      }
      const e = entry as Partial<CharacterSkill>;
      if (typeof e.skillId !== 'string' || e.skillId.length === 0) {
        push(`skills[${i}] is missing skillId.`);
        return;
      }
      if (seen.has(e.skillId)) push(`Skill "${e.skillId}" is listed more than once.`);
      seen.add(e.skillId);
      if (typeof e.rank !== 'number' || !Number.isInteger(e.rank)) {
        push(`Skill "${e.skillId}" has a non-integer Rank.`);
      } else if (e.rank < MIN_SKILL_RANK || e.rank > MAX_SKILL_RANK) {
        push(
          `Skill "${e.skillId}" has Rank ${e.rank}; recorded Skills must be Rank ${MIN_SKILL_RANK}-${MAX_SKILL_RANK} (Rules for Survival p.12, Crawlers & Customization p.6).`,
        );
      }
    });
  }

  if (!isWholeNumber(c['healthBarSlotsRemaining'])) {
    push('healthBarSlotsRemaining must be a whole number of at least 0.');
  } else if ((c['healthBarSlotsRemaining'] as number) > CRAWLER_HEALTH_BAR_SLOTS) {
    push(`healthBarSlotsRemaining cannot exceed ${CRAWLER_HEALTH_BAR_SLOTS} (Rules for Survival p.41).`);
  }
  if (!isWholeNumber(c['currentMana'])) push('currentMana must be a whole number of at least 0.');
  if (!isWholeNumber(c['aiFavor'])) push('aiFavor must be a whole number of at least 0.');

  if (c['crawlerType'] === 'human' || c['crawlerType'] === 'animal') {
    const size = c['size'];
    if (typeof size !== 'number' || !isLegalSize(c['crawlerType'], size)) {
      push(
        `size ${String(size)} is not legal for a ${c['crawlerType']} crawler (Crawlers & Customization p.16, Table 10).`,
      );
    }
  }

  const dying = c['dyingRoundsRemaining'];
  if (dying !== null && !isWholeNumber(dying)) {
    push('dyingRoundsRemaining must be a whole number or null.');
  }

  const origin = c['origin'];
  if (origin !== null && origin !== undefined) {
    if (typeof origin !== 'object') {
      push('origin must be an object or null.');
    } else {
      const o = origin as Record<string, unknown>;
      if (!Array.isArray(o['backgrounds'])) push('origin.backgrounds must be a list.');
      if (typeof o['combat'] !== 'object' || o['combat'] === null) push('origin.combat is missing.');
      if (!Array.isArray(o['grants'])) push('origin.grants must be a list.');
    }
  }

  const marks = c['advancementMarks'];
  if (!Array.isArray(marks)) {
    push('advancementMarks must be a list.');
  } else if (marks.some((m) => typeof m !== 'string')) {
    push('advancementMarks must contain only Skill IDs.');
  } else if (new Set(marks).size !== marks.length) {
    push('advancementMarks contains the same Skill twice.');
  }

  const originHistory = c['origin'];
  if (typeof originHistory === 'object' && originHistory !== null) {
    const o = originHistory as Record<string, unknown>;
    // Typed history, so it cannot quietly disappear the way an incidental
    // extra property would when `set-origin` replaces the object.
    // Historical records are CATALOGUE-INDEPENDENT: a grant may legitimately
    // name an Item this installation does not have, and refusing the crawler
    // for that would lose the history. Only the shape is checked.
    checkGrants(o['grants'], 'origin.grants', push);

    if (!Array.isArray(o['unmatchedGrants'])) {
      push('origin.unmatchedGrants must be a list.');
    } else if (o['unmatchedGrants'].some((g) => typeof g !== 'string')) {
      push('origin.unmatchedGrants must hold only text.');
    }
    if (Array.isArray(o['backgrounds'])) {
      o['backgrounds'].forEach((entry, i) => {
        if (typeof entry !== 'object' || entry === null) return;
        checkGrants(
          (entry as Record<string, unknown>)['grantedItems'],
          `origin.backgrounds[${i}].grantedItems`,
          push,
        );
      });
    }
  }

  const inventory = c['inventory'];
  if (!Array.isArray(inventory)) {
    push('inventory must be a list.');
  } else {
    // RULE DCC-INV-002 - `equipped` is mechanical state, so the invariant that
    // it never exceeds what is owned is checked on the way in. A record with
    // more worn than owned would grant bonuses for items that do not exist.
    for (const problem of validateInventory(inventory as InventoryEntry[])) {
      push(`inventory: ${problem.itemId} ${problem.message}`);
    }
  }

  // Catalogue-independent, and the line it draws is MALFORMED versus
  // UNSATISFIABLE rather than lenient versus strict.
  //
  // REFUSED here, because these are structure and no released pathway could
  // have produced them, so rejecting one discards nothing the application made:
  //   - exactly ten slots, and every entry a valid shape;
  //   - an Item quantity that is a whole number from 1 to 999;
  //   - no two slots naming the same thing (a duplicate would let both slots
  //     report themselves usable off one copy, and would break Undo).
  //
  // PRESERVED, deliberately, because these are dependency and ownership
  // questions whose answers change with the crawler and the installation:
  //   - a reference to content this machine does not have;
  //   - a Spell the crawler no longer knows;
  //   - an allocation larger than the unequipped copies left to back it.
  // A crawler must still open in all three cases, and by RULE DCC-HOT-018 the
  // entry does nothing while it sits there.
  for (const problem of validateHotlist(c['hotlist'])) push(problem);

  const companions = c['companions'];
  if (!Array.isArray(companions)) {
    push('companions must be a list.');
  } else {
    const seenPetIds = new Set<string>();
    companions.forEach((value, i) => {
      if (typeof value !== 'object' || value === null) {
        push(`companions[${i}] is not an object.`);
        return;
      }
      const pet = value as PetCompanion;
      if (typeof pet.id === 'string') {
        if (seenPetIds.has(pet.id)) push(`Pet id "${pet.id}" is listed more than once.`);
        seenPetIds.add(pet.id);
      }
      for (const problem of validatePetCompanion(pet)) push(`companions[${i}]: ${problem}`);
    });
  }

  const changeLog = c['changeLog'];
  if (!Array.isArray(changeLog)) {
    push('changeLog must be a list.');
  } else {
    // Every entry carries the changes that REVERSE it, as a list. A single
    // object here means the record predates the schema 5 -> 6 migration and
    // reached this point without being migrated.
    changeLog.forEach((entry, i) => {
      if (typeof entry !== 'object' || entry === null) {
        push(`changeLog[${i}] is not an object.`);
        return;
      }
      if (!Array.isArray((entry as Record<string, unknown>)['undo'])) {
        push(`changeLog[${i}] does not carry a list of reversing changes.`);
      }
    });
  }

  if (typeof c['notes'] !== 'string') push('notes must be text (it may be empty).');

  return { valid: errors.length === 0, errors };
}

export function assertCharacter(value: unknown): Character {
  const report = validateCharacter(value);
  if (!report.valid) {
    throw new Error(`Invalid character record:\n- ${report.errors.join('\n- ')}`);
  }
  return value as Character;
}

function isPositiveInt(v: unknown): boolean {
  return typeof v === 'number' && Number.isInteger(v) && v >= 1;
}

function isWholeNumber(v: unknown): boolean {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0;
}

/**
 * Checks a list of historical ItemGrant records.
 *
 * One primitive for both the combat-choice grants and each Background's, so the
 * two cannot drift into checking different things.
 *
 * Deliberately does NOT look the Item up. These are records of what happened,
 * and an installation that lacks the definition must still be able to open the
 * crawler; losing the history would be worse than not resolving the name.
 */
function checkGrants(value: unknown, where: string, push: (message: string) => void): void {
  if (!Array.isArray(value)) {
    push(`${where} must be a list.`);
    return;
  }
  value.forEach((entry, i) => {
    const at = `${where}[${i}]`;
    if (typeof entry !== 'object' || entry === null) {
      push(`${at} is not an object.`);
      return;
    }
    const grant = entry as Record<string, unknown>;
    if (typeof grant['itemId'] !== 'string' || grant['itemId'] === '') {
      push(`${at} has no item ID.`);
    }
    const quantity = grant['quantity'];
    if (!Number.isInteger(quantity) || (quantity as number) < 1) {
      push(`${at} must record a whole quantity of at least 1.`);
    }
  });
}
